import { Button } from "../../../../../../../components/ui";
import { useEffect, useRef, useState } from "react";
import type { UseFormSetValue } from "react-hook-form";
import type { Geofence } from "../../../../../../../store/admin/geofencesStore";
import type { ThemeClasses, PointCoordinate } from "../../types";

function CoordinateInput({
  value,
  onChange,
  className,
}: {
  value: number;
  onChange: (next: number) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState(String(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(String(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        setDraft(String(value));
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const parsed = parseFloat(raw.replace(",", "."));
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
      className={className}
    />
  );
}

interface PolygonalCoordinatesFormProps {
  polygonPoints: PointCoordinate[];
  setPolygonPoints: (points: PointCoordinate[]) => void;
  setValue: UseFormSetValue<Geofence>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}

export function PolygonalCoordinatesForm({
  polygonPoints,
  setPolygonPoints,
  setValue,
  themeClasses,
}: PolygonalCoordinatesFormProps) {
  const commitPoints = (newPoints: PointCoordinate[]) => {
    setPolygonPoints(newPoints);
    setValue("coordinates", newPoints);
  };

  const handleRemovePoint = (idx: number) => {
    commitPoints(polygonPoints.filter((_, i) => i !== idx));
  };

  const handleChangeCoordinate = (
    idx: number,
    field: "latitude" | "longitude",
    next: number
  ) => {
    commitPoints(
      polygonPoints.map((p, i) => (i === idx ? { ...p, [field]: next } : p))
    );
  };

  const handleAddPoint = () => {
    const last = polygonPoints[polygonPoints.length - 1];
    commitPoints([
      ...polygonPoints,
      {
        latitude: last ? last.latitude + 0.002 : 19.4326,
        longitude: last ? last.longitude + 0.002 : -99.1332,
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-3">
      <label className={`block text-sm font-medium ${themeClasses.text} mb-1`}>
        Puntos del polígono
      </label>

      {/* Contenedor con scroll */}
      <div className="max-h-60 overflow-y-auto pr-2 space-y-2">
        {polygonPoints.map((point, idx) => (
          <div
            key={idx}
            className={`flex gap-2 items-center p-2 rounded-lg bg-app border ${themeClasses.cardBorder}`}
          >
            {/* Número de punto */}
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-subtle text-content-secondary`}
            >
              {idx + 1}
            </div>

            {/* Coordenada Latitud */}
            <div className="flex-1 min-w-0">
              <label
                className={`w-full text-xs px-3 py-2 bg-card border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } flex items-center gap-2 focus-within:ring-2 focus-within:ring-primary-500`}
              >
                <span className="text-content-muted">Lat:</span>
                <CoordinateInput
                  value={point.latitude}
                  onChange={(next) => handleChangeCoordinate(idx, "latitude", next)}
                  className="flex-1 min-w-0 w-full bg-transparent outline-none"
                />
              </label>
            </div>

            {/* Coordenada Longitud */}
            <div className="flex-1 min-w-0">
              <label
                className={`w-full text-xs px-3 py-2 bg-card border ${themeClasses.cardBorder} rounded-lg ${
                  themeClasses.text
                } flex items-center gap-2 focus-within:ring-2 focus-within:ring-primary-500`}
              >
                <span className="text-content-muted">Lng:</span>
                <CoordinateInput
                  value={point.longitude}
                  onChange={(next) => handleChangeCoordinate(idx, "longitude", next)}
                  className="flex-1 min-w-0 w-full bg-transparent outline-none"
                />
              </label>
            </div>

            <Button
              type="button"
              variant="dangerSoft"
              size="sm"
              iconOnly
              className="shrink-0"
              onClick={() => handleRemovePoint(idx)}
              disabled={polygonPoints.length <= 3}
              title="Eliminar punto"
              icon={<i className="ri-delete-bin-line" />}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button
          type="button"
          size="sm"
          onClick={handleAddPoint}
          icon={<i className="ri-add-line" />}
        >
          Agregar punto
        </Button>

        {polygonPoints.length < 3 && (
          <span className="text-error text-xs">Mínimo 3 puntos</span>
        )}
      </div>
    </div>
  );
}
